/**
 * v25 Phase 2 (BOLT-144) — say what the play area IS, in words a player has.
 *
 * Phase 1 made the polygon true. Nothing about it is LEGIBLE yet: the lobby
 * shows "0.315 mi²", which nobody can picture, and gives no evidence at all
 * that the border follows anything real. Jake's field report is precisely that
 * the map looks arbitrary, so the fix is not a better polygon, it is telling
 * the player what the polygon is made of.
 *
 * Three answers live here, and all three are DERIVED FROM THE RETURNED RING,
 * never from the inputs that produced it:
 *
 *  1. COMPOSITION - "Custer Rd, Rowlett Creek". Sampled off the actual
 *     perimeter, so the label physically cannot describe a different map than
 *     the one on screen. It does two jobs: it proves the border is real, and it
 *     teaches what a good boundary is made of, which is the only way anyone
 *     learns what to look for when drawing their own.
 *  2. TIME TO CROSS - "about a 12 minute walk across", from the SAME speed
 *     model the Phase 1 size target is derived from, so the sentence is
 *     literally a restatement of the number.
 *  3. WHAT TO DO WHEN THERE IS NO MAP - every refusal names at least one
 *     concrete next action. Phase 1 made rural correctly return nothing, which
 *     means the way out of nothing is now a shipped feature, not an edge case.
 *
 * Pure TypeScript: browser, Node and server. The iOS half receives (1) from the
 * server on the wire and ports (2) and (3) as tables, pinned by
 * `boundaryDescribe.test.ts`.
 */
import type { BarrierKind, TravelMode } from '../travelMode';
import { TRAVEL_MODES, TRAVEL_MODE_LABEL } from '../travelMode';
import { DEFAULT_TARGET_MODEL, type TargetModel } from './boundarySelector';
import type { FaceFailure } from './planarFace';
import type { PlanarPoint } from './planarFace';
import { attributeRing } from './ringAttribution';

/* ------------------------------------------------------------------ *
 * 1. composition: what is this border called?
 * ------------------------------------------------------------------ */

/**
 * A line that may carry a name.
 *
 * Names arrive from a DIFFERENT tile layer than the geometry does: in
 * OpenMapTiles the `transportation` layer has no `name` field at all (verified
 * on real tiles: 298 road features at Craig Ranch, 0 named), and the names live
 * in the parallel `transportation_name` layer. `waterway` carries its own name,
 * which is how "Rowlett Creek" gets a label at all. So a name index is a set of
 * lines that are NOT walls, matched to the border by proximity.
 */
export interface NamedLine {
  name: string;
  kind: BarrierKind;
  readonly coordinates: readonly (readonly number[])[];
}

export interface CompositionPart {
  name: string;
  kind: BarrierKind;
  metres: number;
  /** Fraction of the whole perimeter, 0..1. */
  share: number;
}

export interface CompositionOptions {
  /** How many names to keep. Three fits a phone line; four does not. */
  maxParts?: number;
  /** Ignore anything that made less than this fraction of the border. */
  minShare?: number;
  /**
   * How close a named line must run to the border to have made it, metres.
   *
   * `transportation_name` geometry is the same road drawn again, so a real
   * match is metres away, not tens of metres. The bound matters: without it the
   * nearest name always wins and a border made of railway would be labelled
   * with whatever street ran nearest the tracks.
   */
  matchToleranceM?: number;
  stepM?: number;
}

export const DEFAULT_COMPOSITION: Required<CompositionOptions> = {
  maxParts: 3,
  minShare: 0.08,
  matchToleranceM: 25,
  stepM: 20,
};

/**
 * What the ring is made of, biggest contributor first.
 *
 * Only NAMED lines are passed in, and only matches within the tolerance count,
 * so a border that genuinely runs along nothing anybody named comes back empty
 * rather than mislabelled. Empty is an honest answer here; a wrong street name
 * would do the exact opposite of the job.
 */
export function describeComposition(
  ring: readonly (readonly number[])[],
  named: readonly NamedLine[],
  center: PlanarPoint,
  opts: CompositionOptions = {},
): CompositionPart[] {
  const o = { ...DEFAULT_COMPOSITION, ...opts };
  if (!ring.length || !named.length) return [];
  const { totalM, metresByLine } = attributeRing(ring, named, center, {
    stepM: o.stepM,
    maxDistM: o.matchToleranceM,
  });
  if (totalM <= 0) return [];

  // One real road arrives as many tile features (Watters Creek is five separate
  // `waterway` lines at Craig Ranch), so group by NAME before ranking.
  const byName = new Map<string, { metres: number; kind: BarrierKind }>();
  for (const [li, metres] of metresByLine) {
    const line = named[li];
    const nm = line.name.trim();
    if (!nm) continue;
    const cur = byName.get(nm);
    if (cur) cur.metres += metres;
    else byName.set(nm, { metres, kind: line.kind });
  }

  const parts: CompositionPart[] = [];
  for (const [name, v] of byName) parts.push({ name, kind: v.kind, metres: v.metres, share: v.metres / totalM });
  parts.sort((a, b) => b.metres - a.metres || a.name.localeCompare(b.name));
  return parts.filter((p) => p.share >= o.minShare).slice(0, o.maxParts);
}

/**
 * Shorten the common English street words so three names fit one phone line.
 *
 * Whole tokens only, and a deliberately tiny table: "Rue Saint-Louis en l'Île"
 * and "Egelantiersgracht" must pass through untouched, and the way to guarantee
 * that is to know almost nothing.
 */
const ABBREVIATION: Record<string, string> = {
  Road: 'Rd',
  Street: 'St',
  Avenue: 'Ave',
  Boulevard: 'Blvd',
  Drive: 'Dr',
  Parkway: 'Pkwy',
  Highway: 'Hwy',
  Lane: 'Ln',
  Court: 'Ct',
  Place: 'Pl',
  Trail: 'Trl',
};

/**
 * Compass words are abbreviated only in a name long enough to survive it.
 *
 * Measured on real tiles: without this rule "West Street" in rural Vermont came
 * out as "W St", which is not a street name, it is a crossword clue. Three
 * tokens or more and the direction is clearly a qualifier ("North Alma Road" ->
 * "N Alma Rd", "Vermont Route 12 North" -> "Vermont Route 12 N").
 */
const DIRECTION: Record<string, string> = { North: 'N', South: 'S', East: 'E', West: 'W' };

export function shortenName(name: string): string {
  const words = name.split(' ');
  return words
    .map((w) => ABBREVIATION[w] ?? (words.length >= 3 ? (DIRECTION[w] ?? w) : w))
    .join(' ');
}

/** The names, shortened, in perimeter order. This is what goes on the wire. */
export function compositionNames(parts: readonly CompositionPart[]): string[] {
  return parts.map((p) => shortenName(p.name));
}

/**
 * The whole line a player reads: "Bounded by Custer Rd, Rowlett Creek".
 *
 * BOTH platforms build the sentence from this shape (iOS ports the one join),
 * so there is one phrasing, pinned by the parity guard.
 */
export const COMPOSITION_PREFIX = 'Bounded by ';

export function compositionSentence(names: readonly string[]): string {
  return names.length ? COMPOSITION_PREFIX + names.join(', ') : '';
}

/**
 * When nothing on the border has a name, say what KIND of thing it is.
 *
 * A canal block in the Jordaan is bounded by named canals; a block in a new
 * subdivision may genuinely be bounded by roads nobody has labelled yet. Saying
 * "main roads and water" still does the trust job, and it still teaches.
 */
export const KIND_PHRASE: Record<BarrierKind, string> = {
  tertiary: 'side roads',
  secondary: 'main roads',
  primary: 'major roads',
  water: 'water',
  rail: 'the railway',
};

/** Kinds that made at least `minShare` of the perimeter, biggest first. */
export function kindPhrases(mix: Partial<Record<BarrierKind, number>>, minShare = 0.1): string[] {
  const rows = (Object.keys(mix) as BarrierKind[])
    .map((k) => ({ k, v: mix[k] ?? 0 }))
    .filter((r) => r.v >= minShare)
    .sort((a, b) => b.v - a.v);
  return rows.map((r) => KIND_PHRASE[r.k]);
}

/** "main roads and water" — the fallback tail for `compositionSentence`. */
export function joinPhrases(phrases: readonly string[]): string {
  if (phrases.length <= 1) return phrases[0] ?? '';
  return phrases.slice(0, -1).join(', ') + ' and ' + phrases[phrases.length - 1];
}

/* ------------------------------------------------------------------ *
 * 2. size, as time to cross
 * ------------------------------------------------------------------ */

/**
 * How long it takes to cross the area, in minutes, at the mode's own speed.
 *
 * The "width" of an arbitrary polygon is the diameter of the circle with the
 * same area. That is the honest summary: it is exactly the quantity the Phase 1
 * target is built from, since the target IS a disc of reachable ground, so at a
 * perfectly on-target map this number equals two headstarts of travel and the
 * phrasing is a restatement rather than a second model.
 */
export function minutesToCross(
  areaSqMeters: number,
  mode: TravelMode,
  model: TargetModel = DEFAULT_TARGET_MODEL,
): number {
  if (!(areaSqMeters > 0)) return 0;
  const diameterM = 2 * Math.sqrt(areaSqMeters / Math.PI);
  return diameterM / model.speedMps[mode] / 60;
}

/** What the player is doing while crossing it. */
export const CROSSING_VERB: Record<TravelMode, string> = {
  foot: 'walk',
  bike: 'ride',
  car: 'drive',
};

/**
 * Round to something a person would actually say. Nobody says "a 23 minute
 * walk"; they say "about 25 minutes".
 */
export function roundMinutes(min: number): number {
  if (min < 1) return 1;
  if (min < 20) return Math.max(1, Math.round(min));
  return Math.round(min / 5) * 5;
}

/**
 * "About a 12 minute walk across" — the size line, replacing square miles.
 *
 * Jake, on the square-mile label: nobody can picture 0.315 mi². This is the
 * same number, said in the unit the decision is actually made in.
 */
export function crossingLabel(
  areaSqMeters: number,
  mode: TravelMode,
  model: TargetModel = DEFAULT_TARGET_MODEL,
): string {
  if (!(areaSqMeters > 0)) return '';
  const raw = minutesToCross(areaSqMeters, mode, model);
  // A one-block car map really does cross in 30 seconds, and "About a 1 minute
  // drive across" reads as a rounding artefact rather than the truth it is.
  if (raw < 1.5) return `Less than a minute ${CROSSING_VERB[mode]} across`;
  return `About a ${roundMinutes(raw)} minute ${CROSSING_VERB[mode]} across`;
}

/* ------------------------------------------------------------------ *
 * 3. the way out of "no map here"
 * ------------------------------------------------------------------ */

/**
 * The reasons a build can end with nothing.
 *
 * `need-coverage` is not a `FaceFailure`: it is the server's typed
 * `need_coverage` error, meaning "you did not send me enough map", which is a
 * completely different problem from "there is no block here" and has a
 * completely different way out.
 */
export type TurfFailure = FaceFailure | 'need-coverage';

export type TurfNextStep = 'coarser' | 'widen' | 'draw';

/** The next coarser mode, or null at the end of the ladder. */
export function coarserMode(mode: TravelMode): TravelMode | null {
  const i = TRAVEL_MODES.indexOf(mode);
  return i >= 0 && i + 1 < TRAVEL_MODES.length ? TRAVEL_MODES[i + 1] : null;
}

/**
 * What went wrong, in one plain sentence.
 *
 * Deliberately never blames the player and never uses an internal word. The old
 * copy said "Could not auto-build one here", which is true and useless.
 */
export function failureHeadline(reason: TurfFailure): string {
  switch (reason) {
    case 'need-coverage':
      return 'Not enough map loaded to size that yet.';
    case 'no-barriers':
      return 'No roads, water or railway around here to build an area from.';
    case 'not-enclosed':
      return 'Nothing encloses this spot, so there is no block to play in.';
    case 'too-large':
      return 'The nearest enclosed area runs further than we can search.';
    case 'too-dense':
      return 'There is too much detail here to build an area quickly.';
    case 'degenerate':
      return 'The area around this spot did not come out usable.';
  }
}

/**
 * Every failure offers at least one CONCRETE action. This is the acceptance
 * criterion, so it is a function rather than a rendering decision: a screen
 * cannot forget to offer a way out if the way out comes with the error.
 *
 * `draw` is last and always present. It is the floor: whatever the map data
 * says, a player with a finger can always define a play area.
 */
export function nextStepsFor(reason: TurfFailure, mode: TravelMode): TurfNextStep[] {
  const steps: TurfNextStep[] = [];
  // Widening only helps when the search ran out of MAP, not out of barriers.
  if (reason === 'need-coverage' || reason === 'too-large') steps.push('widen');
  // A coarser mode searches further and treats fewer roads as walls, so it is
  // the real answer to "nothing encloses you here" - which is why rural foot
  // fails and rural car does not.
  if (coarserMode(mode) && reason !== 'no-barriers' && reason !== 'need-coverage') steps.push('coarser');
  steps.push('draw');
  return steps;
}

export function nextStepLabel(step: TurfNextStep, mode: TravelMode): string {
  switch (step) {
    case 'widen':
      return 'Look further out';
    case 'coarser': {
      const next = coarserMode(mode);
      return next ? `Try ${TRAVEL_MODE_LABEL[next].toLowerCase()}` : 'Try a bigger area';
    }
    case 'draw':
      return 'Draw it yourself';
  }
}
